import React, { useEffect, useState } from "react";
import { types, useAdminContext, useReactBricksContext } from "react-bricks/frontend";
import { mobile } from "components/templates/Breakpoints";
import { color } from "@styles";
import { P1 } from "components/atoms/Paragraph";
import { Form } from "@unform/web";
import styled from "styled-components";
import { Link as _Link } from "@components/atoms";
import { QuoteSubmit } from "components/pages/GetAQuote/QuoteSubmit";
import HubspotFormField, { HubspotFormFieldProps } from "./HubspotFormField";
import useHubspotFormState from "./useHubspotFormState";
import { sortBy } from "lodash";
import { SUPPORTED_DYNAMIC_FORMS } from "data/forms";

export interface HubspotFormProps {
  redirectUrl: string;
  formId: string;
  hasFetched?: boolean;
  formIdOverride?: string;
  fields: types.RepeatserItems<HubspotFormFieldProps>;
}

type HubspotFormField = {
  label: string;
  objectTypeId: string;
  name: string;
  required: boolean;
  hidden: boolean;
  placeholder: string;
  fieldType: string;
  options?: {
    label: string;
    value: string;
    displayOrder: number;
  }[];
};

type HubspotForm = {
  id: string;
  fieldGroups: {
    groupType: string;
    richTextType: string;
    fields: HubspotFormField[];
  }[];
};

const fieldTypeMap = (
  input: string,
): "text" | "phone" | "email" | "select" | "multiselect" | "textarea" => {
  switch (input) {
    case "single_line_text":
      return "text";
    case "dropdown":
      return "select";
    case "multiple_checkboxes":
      return "multiselect";
    case "text_area":
      return "textarea";
    default:
      return input as "text" | "phone" | "email" | "select" | "multiselect" | "textarea";
  }
};

const HubspotAutoForm: types.Brick<HubspotFormProps> = ({
  formIdOverride,
  formId,
  redirectUrl,
}) => {
  const { fields, hasFetched } = useGetExternalData({ formId: formIdOverride || formId });
  const { isAdmin } = useAdminContext();

  const { submit, formRef, error, loading, isShaking } = useHubspotFormState({
    fields,
    formId: formIdOverride || formId,
    redirectUrl,
  });

  if (!hasFetched) {
    return <Wrapper>{isAdmin && <P1>Select a form ID and press save to preview</P1>}</Wrapper>;
  }

  return (
    <Wrapper>
      <FormContainer
        ref={formRef}
        onSubmit={submit}
        placeholder=""
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        {fields.map((field) => (
          <HubspotFormField key={field.name} {...field} />
        ))}

        <QuoteSubmit value="Submit" type="submit" isInputValid={!isShaking} loading={loading} />
        {error && <P1 color="#FA5252">{error}</P1>}
        <P1 color={color.B2100}>
          By submitting your information, you agree to YuLife's{" "}
          <Link passHref href="/privacy-policy/">
            Privacy Policy.
          </Link>
        </P1>
        <P1 color={color.B2100} align="right">
          <Asterisk />
          Required fields
        </P1>
      </FormContainer>
    </Wrapper>
  );
};

// Not working in bricks, need to async fetch
const useGetExternalData = ({ formId }) => {
  const [hubspotForm, setHubspotForm] = useState<HubspotForm>();
  const [fields, setFields] = useState<HubspotFormFieldProps[]>([]);
  const [fetched, setFetched] = useState(false);

  if (formId === "none") {
    return {};
  }

  useEffect(() => {
    if (!formId) {
      return;
    }

    fetch(`/api/hubspot/form`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        formId,
      }),
    })
      .then((result) => {
        result.json().then((_hubspotForm) => {
          const _fields = (_hubspotForm?.fieldGroups || [])
            .map((f) => f.fields)
            .filter((f) => !!f)
            .flat()
            .filter((f) => !f.hidden)
            .map((f: HubspotFormField) => {
              return {
                label: f.label || f.placeholder,
                type: fieldTypeMap(f.fieldType),
                name: f.objectTypeId === "0-1" ? f.name : `${f.objectTypeId}/${f.name}`,
                isRequired: f.required,
                options: f.options
                  ? sortBy(f.options, "displayOrder").map(({ label, value }) => ({ label, value }))
                  : null,
                objectTypeId: f.objectTypeId,
              };
            });

          setFields(_fields);
          setHubspotForm(_hubspotForm);
          setFetched(true);
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }, [formId]);

  return {
    fields,
    formId: hubspotForm?.id,
    hasFetched: fetched,
    hubspotForm,
  };
};

HubspotAutoForm.schema = {
  // Unique brick name
  name: "hubspot-form",
  label: "Hubspot Form",

  // Defaults when a new brick is added
  getDefaultProps: () => ({
    redirectUrl: "/form/success",
  }),

  // Sidebar Edit controls for props
  sideEditProps: [
    {
      name: "formId",
      label: "Hubspot Form",
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: [
          {
            value: "none",
            label: "Select a form",
          },
          ...Object.keys(SUPPORTED_DYNAMIC_FORMS).map((form) => ({
            label: form,
            value: SUPPORTED_DYNAMIC_FORMS[form],
          })),
          {
            value: "custom",
            label: "Custom",
          },
        ],
      },
    },
    {
      name: "formIdOverride",
      label: "Custom Form ID",
      type: types.SideEditPropType.Text,
      show: (props) => props?.formId === "custom",
    },
    {
      name: "redirectUrl",
      label: "Redirect on success",
      type: types.SideEditPropType.Text,
    },
  ],
};

export default HubspotAutoForm;

const Wrapper = styled.div`
  max-width: 800px;
  padding: 1rem;
`;

export const Grid = styled.div<{ gap?: string }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  gap: ${(p) => p.gap || "8rem"};

  ${mobile} {
    grid-template-columns: 1fr;
    row-gap: 0;
  }
`;

export const FormContainer = styled(Form)`
  background: ${color.B210};
  border: 2px solid ${color.B240};
  border-radius: 8px;
  box-shadow: 0.5rem 0.5rem 0px rgba(92, 87, 87, 0.08);
  padding: 2.5rem;

  ${mobile} {
    padding: 1.25rem;
    transform: translateY(0);
  }
`;

export const Asterisk = styled.span`
  &:before {
    content: "*";
    color: #ff6666;
  }
`;

const Link = styled(_Link)`
  color: ${color.P100};
  text-decoration: underline;
`;
